import {Link} from '@tanstack/react-router'

// Flight booking section
import flyBookingAccessibleImg from '@/assets/homepage/fly-booking-accessible.png?minW=300&picture-image-tools'
import flyBookingHelpImg from '@/assets/homepage/fly-booking-help.png?minW=300&picture-image-tools'
import flyBookingNeedImg from '@/assets/homepage/fly-booking-needs.png?minW=300&picture-image-tools'
import flyBookingPlaneImg from '@/assets/homepage/fly-booking-plane.png?minW=920&picture-image-tools'
import flyBookingPlaneMobileImg from '@/assets/homepage/fly-booking-plane-mobile.png?minW=300&picture-image-tools'
// Hero
import heroBackgroundLqip from '@/assets/homepage/hero-background.jpeg?lqip'
import heroBackgroundImg from '@/assets/homepage/hero-background.jpeg?minW=1024&picture-image-tools'
import heroBackgroundMobileLqip from '@/assets/homepage/hero-background-mobile.jpg?lqip'
import heroBackgroundMobileImg from '@/assets/homepage/hero-background-mobile.jpg?minW=420&picture-image-tools'
// Icons
import AccessibilitySvg from '@/assets/homepage/icon/accessibility.svg?react'
import CartSvg from '@/assets/homepage/icon/cart.svg?react'
import ControllerSvg from '@/assets/homepage/icon/controller.svg?react'
import HeadphoneSvg from '@/assets/homepage/icon/headphones.svg?react'
import HeartWaveSvg from '@/assets/homepage/icon/heart-waves.svg?react'
import UiClickSvg from '@/assets/homepage/icon/mynaui_click.svg?react'
import SearchSvg from '@/assets/homepage/icon/search.svg?react'
import PhoneSvg from '@/assets/homepage/icon/telephone-call.svg?react'
import UsersGroupSvg from '@/assets/homepage/icon/users-group.svg?react'
import shopPhoneImg from '@/assets/homepage/tele-phone.png?minW=375&picture-image-tools'
// Flags
import flagAusSvg from '@/assets/icons/flags/australia.svg'
import flagCanadaSvg from '@/assets/icons/flags/canada.svg'
import flagUkSvg from '@/assets/icons/flags/united-kingdom.svg'
import flagUsSvg from '@/assets/icons/flags/united-states.svg'
import successSvg from '@/assets/icons/success.svg'
// Social mentions
import acbImg from '@/assets/images/media-mentions/acb.png'
import acbDarkImg from '@/assets/images/media-mentions/acb-dark.png'
import afbImg from '@/assets/images/media-mentions/afb.png'
import afbDarkImg from '@/assets/images/media-mentions/afb-dark.png'
import beMyEyesImg from '@/assets/images/media-mentions/bemyeyes.png'
import beMyEyesDarkImg from '@/assets/images/media-mentions/bemyeyes-dark.png'
import blindlifeImg from '@/assets/images/media-mentions/blindlife.png'
import blindlifeDarkImg from '@/assets/images/media-mentions/blindlife-dark.png'
import brailleInstituteImg from '@/assets/images/media-mentions/braille-institute.png'
import brailleInstituteDarkImg from '@/assets/images/media-mentions/braille-institute-dark.png'
import doubletapImg from '@/assets/images/media-mentions/doubletap.png'
import doubletapDarkImg from '@/assets/images/media-mentions/doubletap-dark.png'
import fightingBlindnessImg from '@/assets/images/media-mentions/fighting-blindness.png'
import fightingBlindnessDarkImg from '@/assets/images/media-mentions/fighting-blindness-dark.png'
import innovativeimpactImg from '@/assets/images/media-mentions/innovativeimpact.png'
import innovativeimpactDarkImg from '@/assets/images/media-mentions/innovativeimpact-dark.png'
import nfbImg from '@/assets/images/media-mentions/nfb.png'
import nfbDarkImg from '@/assets/images/media-mentions/nfb-dark.png'
import thatRealBlindTechShowImg from '@/assets/images/media-mentions/thatrealblindtechshow.png'
import thatRealBlindTechShowDarkImg from '@/assets/images/media-mentions/thatrealblindtechshow-dark.png'
import vistaCenterImg from '@/assets/images/media-mentions/vista-center.png'
import vistaCenterDarkImg from '@/assets/images/media-mentions/vista-center-dark.png'
import Picture from '@/components/Picture'
import SkipLink from '@/components/SkipLink'
import Navbar from '@/layout/Navbar'
import {type SearchInputControl} from '@/layout/SearchInputWithAutocomplete'
import HeadTags from '@/lib/head/HeadTags'
import {type SupportedLocales, useLocale} from '@/lib/locale/locale'
import {Theme} from '@/lib/theme/theme'
import {useCurrentTheme} from '@/lib/theme/useCurrentTheme'
import {FlightRoute, HomeRoute} from '@/routeRegistry'
import {FLIGHT_ROUTE_DEFAULT_SEARCH} from '@/routes/_flight/flights/index'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import call from '@/utils/api/call'
import shouldLogError from '@/utils/api/shouldLogError'

import style from './Index.module.scss'

const Footer = lazy(async () => await import('@/layout/Footer'))
//CONTENT
const INTRODUCTION = [
  {
    lable: 'Simplicity',
    Icon: UiClickSvg,
    content:
      'Everything is designed to be easy to use, so you can get what you need without confusion or extra steps.',
  },
  {
    lable: 'Accessibility',
    Icon: AccessibilitySvg,
    content:
      'A complete, start-to-finish experience where you can browse, shop, and check out without ever leaving the site.',
  },
  {
    lable: 'Community',
    Icon: UsersGroupSvg,
    content:
      'We focus on our users, especially those who are blind or have low vision, with care and understanding.',
  },
]

const SHOPPING = [
  {
    lable: 'Easy Shopping',
    content: 'Simple search, quick checkout, and essential info—no distractions.',
    status: 0,
  },
  {
    lable: 'Smart AI Help',
    content: 'Compare, discover, and shop using just your voice or keyboard.',
    status: 1,
  },
  {
    lable: 'Massive Selection',
    content: 'Billions of products, 500,000 stores—no ads, just choices!',
    status: 0,
  },
]

const TELEPHONE: Record<
  SupportedLocales,
  {number: string; label: string; image: string; tag: string}
> = {
  en_US: {
    label: '+1 855-746-7478 (U.S)',
    tag: 'us-tel-number',
    number: '+18557467478',
    image: flagUsSvg,
  },
  en_GB: {
    label: '+44 7727 247467 (U.K)',
    tag: 'gb-tel-number',
    number: '+447727247467',
    image: flagUkSvg,
  },
  en_CA: {
    label: '+1 782 222 7467 (CA)',
    tag: 'ca-tel-number',
    number: '+17822227467',
    image: flagCanadaSvg,
  },
  en_AU: {
    label: '+61 489 267 467 (AU)',
    tag: 'au-tel-number',
    number: '+61489267467',
    image: flagAusSvg,
  },
}

const SHOP_BY_VOICE = [
  {
    lable: 'Explore Billions',
    content: 'Shop billions of products from 500,000 stores by talking!',
    Icon: SearchSvg,
  },
  {
    lable: 'Hands-Free Shopping',
    content: 'Find, compare, and buy—just with your voice!',
    Icon: CartSvg,
  },
  {
    lable: 'Pay by Phone',
    content: 'Secure checkout with any major credit card, no internet needed!',
    Icon: PhoneSvg,
  },
]

const FLIGHT_BOOKING = [
  {
    tag: 'all-airports-all-airlines-desktop',
    lable: 'All Airports, All Airlines',
    content: ' Find flights fast, no extra searching.',
    image: flyBookingPlaneImg,
  },
  {
    tag: 'all-airports-all-airlines-mobile',
    lable: 'All Airports, All Airlines',
    content: ' Find flights fast, no extra searching.',
    image: flyBookingPlaneMobileImg,
  },
  {
    tag: 'fully-accessibility',
    lable: 'Fully Accessible',
    content: 'Book start to finish without leaving Innosearch.',
    image: flyBookingAccessibleImg,
  },
  {
    tag: 'special-needs',
    lable: 'Special Needs',
    content: 'Add guide dog, mobility, or medical details—we notify the airline.',
    image: flyBookingNeedImg,
  },
  {
    tag: 'instant-help',
    lable: ' Instant Help',
    content: 'Get flight info anytime from AI or live support!',
    image: flyBookingHelpImg,
  },
] as const

const TRUSTED = [
  {
    label: 'Sam Seavey Influencer',
    image: {
      light: blindlifeImg,
      dark: blindlifeDarkImg,
    },
    link: 'https://www.youtube.com/watch?v=-rjL-HAwaZY',
    size: 'normal',
  },
  {
    label: 'Double Tap',
    image: {
      light: doubletapImg,
      dark: doubletapDarkImg,
    },
    link: 'https://www.youtube.com/watch?v=5HgEkuHogQc',
    size: 'normal',
  },
  {
    label: 'InnovativeImpact Monthly Webinar',
    image: {
      light: innovativeimpactImg,
      dark: innovativeimpactDarkImg,
    },
    link: 'https://drkirkadams.com/about/',
    size: 'normal',
  },
  {
    label: 'Co-hosted VistaCenter Conference',
    image: {
      light: vistaCenterImg,
      dark: vistaCenterDarkImg,
    },
    link: 'https://vistacenter.org/',
    size: 'normal',
  },
  {
    label: 'Accessible Holiday Shopping Show with InnosearchAI and ACB',
    image: {
      light: acbImg,
      dark: acbDarkImg,
    },
    link: 'https://www.acb.org/home',
    size: 'normal',
  },
  {
    label: 'Braille Institute',
    image: {
      light: brailleInstituteImg,
      dark: brailleInstituteDarkImg,
    },
    link: 'https://www.brailleinstitute.org/',
    size: 'normal',
  },
  {
    label: 'Foundation Fighting Blindness',
    image: {
      light: fightingBlindnessImg,
      dark: fightingBlindnessDarkImg,
    },
    link: 'https://www.fightingblindness.org/',
    size: 'normal',
  },
  {
    label: 'American Foundation for the Blind',
    image: {
      light: afbImg,
      dark: afbDarkImg,
    },
    link: 'https://www.afb.org/',
    size: 'normal',
  },
  {
    label: 'That Real Blind Tech Show',
    image: {
      light: thatRealBlindTechShowImg,
      dark: thatRealBlindTechShowDarkImg,
    },
    link: 'https://thatrealblindtechshow.libsyn.com/episode-168-innosearchai-the-future-of-blind-online-shopping-is-here',
    size: 'large',
  },
  {
    label: 'Be My Eyes',
    image: {
      light: beMyEyesImg,
      dark: beMyEyesDarkImg,
    },
    link: 'https://www.bemyeyes.com/news/be-my-eyes-to-provide-in-app-accessible-shopping/',
    size: 'normal',
  },
  {
    label: 'National Federation of the Blind',
    image: {
      light: nfbImg,
      dark: nfbDarkImg,
    },
    link: 'https://nfb.org/about-us/press-room/innosearch-ai-and-national-federation-blind-announce-strategic-partnership',
    size: 'large',
  },
] as const

const COMMUNITY = [
  {
    lable: 'Game Nights',
    content: 'Play trivia, escape rooms & more—win prizes!',
    Icon: ControllerSvg,
    status: 0,
  },
  {
    lable: 'Learn & Train',
    content: 'Hands-on accessibility tool training.',
    Icon: HeartWaveSvg,
    status: 1,
  },
  {
    lable: 'Give Feedback',
    content: 'Test new features & share your thoughts.',
    Icon: HeadphoneSvg,
    status: 0,
  },
]

export default memo(function Index() {
  const [currentTheme] = useCurrentTheme()
  const [locale] = useLocale()
  const [isSuccess, setIsSuccess] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const searchInputRef = useRef<SearchInputControl>(null)

  //HANDLE SUBMIT INTO COMMUNITY
  const handleSubmitCommunity = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (e.currentTarget.checkValidity()) {
      call
        .post('/api/feedback/', {
          data: {
            feedback_type: 'form',
            email,
            phone: '',
            full_name: name,
            content: 'message',
          },
        })
        .then(() => {
          setIsSuccess(true)
        })
        .catch(e => {
          if (shouldLogError(e)) {
            console.error('Cannot send feedback', e)
          }
        })
    }
  }

  const handleSearchReady = useCallback(() => {
    searchInputRef.current?.focus()
  }, [])

  return (
    <div>
      <HeadTags title='Home' />
      <SkipLink title='Skip to main content' to='#main-content' />
      <Navbar
        variant='dark'
        searchInputRef={searchInputRef}
        withSearch
        onSearchReady={handleSearchReady}
      />
      <main {...skipTargetProps('main-content')} className={style.Page}>
        <div className={style.HeroContainer}>
          {/* Fullscreen image, don't need to specify `sizes` */}
          <Picture
            src={[
              {
                pic: heroBackgroundImg,
                lqip: heroBackgroundLqip,
                media: 'min-width: 64rem',
              },
              {
                pic: heroBackgroundMobileImg,
                lqip: heroBackgroundMobileLqip,
                media: 'max-width: 63.9375rem',
              },
            ]}
            fetchPriority='high'
            alt='Accessible Digital Services'
            className={clsx(style.Hero)}
            preload
          />
          <div className={style.HeroContent}>
            <h1 className={style.Heading}>Accessible Digital Services</h1>
            <h2 className={style.SubHeading}>Empowering the underserved online.</h2>
          </div>
        </div>
        <div className={style.PageContent}>
          <section className={style.Section1}>
            <div className={style.Section1List}>
              {INTRODUCTION.map(item => {
                return (
                  <div key={item.lable} className={style.Section1Item}>
                    <item.Icon className={style.Section1ItemIcon} aria-label='' />
                    <h3 className={style.Section1ItemHeading}>{item.lable}</h3>
                    <p className={style.Section1ItemContent}>{item.content}</p>
                  </div>
                )
              })}
            </div>
          </section>
          <section className={style.Section2}>
            <h2 className={style.Section2Heading}>Shopping</h2>
            <div className={style.Section2List}>
              {SHOPPING.map(item => (
                <div
                  key={`shopping-item-${item.lable}`}
                  className={`${style.Section2Item} ${item.status === 1 ? style.activeSection2Item : ''}`}
                >
                  <h3 className={style.Section2ItemHeading}>{item.lable}</h3>
                  <p className={style.Section2ItemContent}>{item.content}</p>
                </div>
              ))}
            </div>
            <div>
              <Link to={HomeRoute.fullPath} role='button' className={style.CtaBtn}>
                Shop now
              </Link>
            </div>
          </section>
          <section className={style.Section3}>
            <h2 className={style.Section3Heading}>+1 855-SHOP-GPT</h2>

            <div className={style.Section3HotlineList}>
              {Object.entries(TELEPHONE).map(([locale, item]) => (
                <div key={locale} className={style.Section3HotlineItem}>
                  <img className={style.Section3HotlineItemImage} src={item.image} alt='' />
                  <p className={style.Section3HotlineItemContent}>{item.label}</p>
                </div>
              ))}
            </div>

            <div className={style.Section3Content}>
              <Picture
                src={{
                  pic: shopPhoneImg,
                }}
                sizes='(max-width: 1023px) 23.4375rem, 47.657rem'
                loading='lazy'
                alt='Accessible Digital Services'
                className={style.Section3ContentImage}
              />
              <div className={style.Section3ContentInfo}>
                <h3 className={style.Section3ContentHeading}>Shop by Voice!</h3>
                <div className={style.Section3ContentList}>
                  {SHOP_BY_VOICE.map(item => {
                    return (
                      <div key={item.lable} className={style.Section3ContentItem}>
                        <item.Icon className={style.Section3ContentItemIcon} aria-label='' />

                        <div className={style.Section3ContentItemValue}>
                          <h4 className={style.ItemHeading}>{item.lable}</h4>
                          <p className={style.ItemValue}>{item.content}</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
                <div>
                  <a
                    // eslint-disable-next-line security/detect-object-injection -- ingore
                    href={`tel:${TELEPHONE[locale].number}`}
                    role='button'
                    className={style.CtaBtn}
                  >
                    Call now
                  </a>
                </div>
              </div>
            </div>
          </section>
          <section className={style.Section4}>
            <h2 className={style.Heading}>Flight Booking</h2>
            <div className={style.ListItemActive}>
              <Picture
                src={{
                  pic: FLIGHT_BOOKING[0].image,
                }}
                sizes='90dvw'
                className={style.ListItemActiveImage}
                loading='lazy'
              />
              <div className={style.ListItemActiveContent}>
                <h3 className={style.ListItemActiveHeading}>{FLIGHT_BOOKING[0].lable}</h3>
                <p className={style.ListItemActiveValue}>{FLIGHT_BOOKING[0].content}</p>
              </div>
            </div>
            <div className={style.Section4List}>
              {FLIGHT_BOOKING.slice(1, 5).map(item => (
                <div key={`fly-booking-${item.tag}`} className={style.ListItem}>
                  <Picture
                    src={{
                      pic: item.image,
                    }}
                    className={style.ListItemImage}
                    sizes='(max-width: 1023px) calc((90dvw - 1.375rem) / 2), calc((90dvw - 2.75rem) / 3)'
                    loading='lazy'
                  />
                  <div className={style.ListItemContent}>
                    <h3 className={style.ListItemHeading}>{item.lable}</h3>
                    <p className={style.ListItemValue}>{item.content}</p>
                  </div>
                </div>
              ))}
            </div>

            <div>
              <Link
                search={FLIGHT_ROUTE_DEFAULT_SEARCH}
                to={FlightRoute.fullPath}
                role='button'
                className={style.CtaBtn}
              >
                Book now
              </Link>
            </div>
          </section>
          <section className={style.Section5}>
            <h3 className={style.Section5Title}>Community focus</h3>
            <h2 className={style.Section5Heading}>
              <span className={style.Section5HeadingStyle}>Join the Innosearch AI Community!</span>{' '}
              🎉
            </h2>
            <div className={style.Section5List}>
              {COMMUNITY.map(item => {
                return (
                  <div
                    key={`community-key-item-${item.lable}`}
                    className={`${style.Section5Item} ${item.status === 1 ? style.Section5ItemActive : ''}`}
                  >
                    <item.Icon className={style.Section5ItemIcon} aria-label='' />
                    <div className={style.Section5ItemContent}>
                      <h4 className={style.Section5ItemHeading}>{item.lable}</h4>
                      <p className={style.Section5ItemValue}>{item.content}</p>
                    </div>
                  </div>
                )
              })}
            </div>
            <h3 className={style.Section5Title}>Totally free! Sign up & we’ll reach out.</h3>

            {isSuccess ? (
              <div className={style.Section5FormSuccess} role='alert'>
                <img src={successSvg} alt='' className={style.Section5FormSuccessIcon} />
                <p className={style.Section5FormSuccessHeading}>Signed up successfully!</p>
              </div>
            ) : (
              // <div className={style.Section5Form}>
              <form onSubmit={handleSubmitCommunity} className={style.Section5Form}>
                <div className={style.FormItem}>
                  {/* <label for='What is your name?*' className={style.FromItemHeading} /> */}
                  <label htmlFor='name' className={style.FromItemHeading}>
                    What is your name?*
                  </label>
                  <input
                    id='name'
                    required
                    type='text'
                    placeholder='Full name'
                    onChange={e => {
                      setName(e.currentTarget.value)
                    }}
                    className={style.FormItemInput}
                  />
                </div>
                <div className={style.FormItem}>
                  {/* <div className={style.FromItemHeading}>What is your email?*</div> */}
                  <label htmlFor='email' className={style.FromItemHeading}>
                    What is your email?*
                  </label>
                  <input
                    id='email'
                    required
                    type='email' // Use type="email" for better validation
                    placeholder='Email address'
                    onChange={e => {
                      setEmail(e.currentTarget.value)
                    }}
                    className={style.FormItemInput}
                  />
                </div>
                <button type='submit' className={style.CtaBtn}>
                  Join us
                </button>
              </form>
              // </div>
            )}
          </section>
          <section className={style.Section6}>
            <div className={style.Heading}>Trusted by</div>

            <div className={style.Section6List}>
              {TRUSTED.map(item => {
                const image = currentTheme === Theme.Dark ? item.image.dark : item.image.light

                return (
                  <div
                    key={image}
                    className={clsx(style.ListItem, item.size === 'large' && style.LargeLogo)}
                  >
                    <a href={item.link} target='_blank' rel='noopener noreferrer'>
                      <img
                        src={image}
                        alt={item.label}
                        className={style.ListItemImage}
                        loading='lazy'
                      />
                    </a>
                  </div>
                )
              })}
            </div>
          </section>
        </div>
      </main>
      <Suspense>
        <Footer />
      </Suspense>
    </div>
  )
})
